import { useEffect, useMemo, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowLeft, Download, LoaderCircle } from 'lucide-react';
import baseapi from '../../baseapi';

type MinutePoint = { minute: number; time: string; count: number };
type DayData = { date: string; counted_total: number; points: MinutePoint[]; loaded_at: number };
type MinuteSeries = { date: string; source: string; points: MinutePoint[]; counted_total: number; manual_adjustment_total?: number };
const IST_OFFSET_SECONDS = 5 * 60 * 60 + 30 * 60;
const dateInIst = (epochSeconds: number) => new Date((epochSeconds + IST_OFFSET_SECONDS) * 1000).toISOString().slice(0, 10);
const shiftDate = (day: string, offset: number) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
};
const number = (v: number | null | undefined) => v == null ? '—' : Math.round(v).toLocaleString('en-IN');
const dateLabel = (v: string) => new Date(`${v}T00:00:00Z`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
const colors = ['#e6781b', '#4d8dba', '#8a57b5', '#178a67'];
const TODAY_TTL_MS = 60000;

// finished days never change: kept for the session, so switching 1 / 2 / 4 days only fetches what is new
const dayCache = new Map<string, { at: number; data: DayData }>();

async function loadDay(date: string, today: string): Promise<DayData> {
  const hit = dayCache.get(date);
  if (hit && (date !== today || Date.now() - hit.at < TODAY_TTL_MS)) return hit.data;
  const { data: series } = await baseapi.get<MinuteSeries>('dashboard/darshan-completed/minutes', { params: { day: date }, timeout: 30000 });
  const points = series?.points || [];
  const data: DayData = { date: series?.date || date, points, loaded_at: Date.now() / 1000,
    counted_total: series?.counted_total ?? points.reduce((sum, p) => sum + p.count, 0) };
  dayCache.set(date, { at: Date.now(), data });
  return data;
}

export function DarshanCompletedDetails({ onClose }: { onClose: () => void }) {
  const [days, setDays] = useState<DayData[]>([]);
  const [range, setRange] = useState<1 | 2 | 4>(2);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let alive = true;
    const today = dateInIst(Date.now() / 1000);
    const dates = Array.from({ length: range }, (_, i) => shiftDate(today, -i));
    Promise.allSettled(dates.map(date => loadDay(date, today)))
      .then(results => {
        if (!alive) return;
        const loaded = results.flatMap(r => r.status === 'fulfilled' ? [r.value] : []);
        if (loaded.length) setDays(loaded);
        else setError('Could not load Darshan Completed minute counts. Please try again.');
      })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [range]);

  const changeRange = (value: 1 | 2 | 4) => {
    if (value === range) return;
    setBusy(true);
    setError('');
    setRange(value);
  };

  const chart = useMemo(() => {
    if (!days.length) return [];
    // today's minutes that have not happened yet stay empty, so its line ends at the current minute instead of 0
    const nowS = Math.max(...days.map(day => day.loaded_at));
    const today = dateInIst(nowS);
    const nowMinute = Math.floor(((nowS + IST_OFFSET_SECONDS) % 86400) / 60);
    return Array.from({ length: 1440 }, (_, minute) => {
      const point: Record<string, number | string | null> = { minute, time: `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}` };
      days.forEach((day, i) => { point[`day${i}`] = day.date === today && minute > nowMinute ? null : day.points[minute]?.count ?? 0; });
      return point;
    });
  }, [days]);

  const downloadExcelCsv = () => {
    if (!days.length) return;
    const rows = [
      ['Minute (IST)', ...days.map(day => day.date)],
      ...chart.map(p => [String(p.time), ...days.map((_, i) => String(p[`day${i}`] ?? ''))]),
      ['Total', ...days.map(day => String(day.counted_total))],
    ];
    const csv = '\uFEFF' + rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `darshan-completed-${days[days.length - 1].date}-to-${days[0].date}.csv`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <main className="fixed inset-0 z-[100] overflow-hidden bg-[#f4f5f8] p-2 sm:p-5 dark:bg-[#111215]">
      <section aria-labelledby="darshan-completed-title" className="mx-auto flex h-full w-full flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl dark:border-[#303541] dark:bg-[#11151d]">
        <header className="flex flex-wrap items-center gap-3 px-4 sm:px-6 py-3 border-b border-neutral-200 dark:border-[#303541]">
          <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm font-bold text-neutral-800 hover:bg-neutral-100 dark:border-[#303541] dark:text-neutral-100 dark:hover:bg-[#202631]">
            <ArrowLeft className="h-4 w-4" /> Back to Overview
          </button>
          <div className="min-w-0 flex-1">
            <h2 id="darshan-completed-title" className="text-lg sm:text-xl font-black text-neutral-900 dark:text-white">Darshan Completed · minute by minute</h2>
            <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">Last {range} {range === 1 ? 'day' : 'days'} · Asia/Kolkata</p>
          </div>
          <div className="flex rounded-lg bg-neutral-100 dark:bg-[#202631] p-1" aria-label="Date range">
            {([1, 2, 4] as const).map(value => <button key={value} type="button" onClick={() => changeRange(value)} aria-pressed={range === value} className={`rounded-md px-3 py-1.5 text-xs font-bold ${range === value ? 'bg-white dark:bg-[#343c4a] text-emerald-700 dark:text-emerald-300 shadow' : 'text-neutral-600 dark:text-neutral-300'}`}>{value} {value === 1 ? 'day' : 'days'}</button>)}
          </div>
          <button type="button" onClick={downloadExcelCsv} disabled={!days.length} className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-3 py-2 text-sm font-bold text-white disabled:opacity-50 hover:bg-emerald-800">
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">Download Excel</span><span className="sm:hidden">Excel</span>
          </button>
        </header>

        {error && !days.length ? <div className="p-8 text-center font-semibold text-rose-600">{error}</div> : !days.length ? <div className="flex min-h-64 items-center justify-center gap-2 text-neutral-600 dark:text-neutral-300"><LoaderCircle className="h-5 w-5 animate-spin" /> Loading minute counts…</div> : <>
          <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 px-4 sm:px-6 pt-2.5 text-xs font-bold">
            {days.map((day, i) => <span key={day.date} className="inline-flex items-center gap-1.5" style={{ color: colors[i] }}><span className="h-2.5 w-2.5 rounded-full" style={{ background: colors[i] }} />{dateLabel(day.date)}</span>)}
            {busy && <span className="inline-flex items-center gap-1 font-medium text-neutral-500"><LoaderCircle className="h-3.5 w-3.5 animate-spin" /> Updating…</span>}
          </div>

          <div className="h-52 shrink-0 px-2 sm:px-5 pt-1" aria-label="Darshan Completed per-minute area chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chart} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#94a3b8" opacity={0.25} />
                <XAxis dataKey="time" interval={119} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={36} />
                <Tooltip />
                {days.map((day, i) => <Area key={day.date} type="linear" dataKey={`day${i}`} name={dateLabel(day.date)} stroke={colors[i]} fill={colors[i]} fillOpacity={0.14} strokeWidth={1.5} dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />)}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="mx-4 sm:mx-6 mt-2 mb-2 min-h-[12rem] flex-1 overflow-auto rounded-xl border border-neutral-200 dark:border-[#303541]">
            <table className="w-full table-fixed border-collapse text-xs sm:text-sm">
              <colgroup>
                <col style={{ width: '22%' }} />
                {days.map(day => <col key={day.date} style={{ width: `${78 / days.length}%` }} />)}
              </colgroup>
              <thead className="sticky top-0 z-10 bg-neutral-100 dark:bg-[#202631] text-neutral-700 dark:text-neutral-200">
                <tr><th className="px-3 py-1.5 text-left">Minute (IST)</th>{days.map((day, i) => <th key={day.date} className="px-2 py-1.5 text-right font-bold" style={{ color: colors[i] }}>{dateLabel(day.date)}</th>)}</tr>
              </thead>
              <tbody>
                {chart.map(p => <tr key={p.minute} className="odd:bg-white even:bg-neutral-50 hover:bg-amber-50 dark:odd:bg-[#11151d] dark:even:bg-[#171c25] dark:hover:bg-[#232a36]">
                  <td className="px-3 py-0.5 font-semibold tabular-nums text-neutral-800 dark:text-neutral-100">{p.time}</td>{days.map((day, i) => <td key={day.date} className="px-3 py-0.5 text-right tabular-nums text-neutral-700 dark:text-neutral-200">{number(p[`day${i}`] as number | null)}</td>)}
                </tr>)}
              </tbody>
            </table>
          </div>
          <p className="shrink-0 px-4 sm:px-6 pb-2 text-[11px] text-neutral-500 dark:text-neutral-400">Minute values are non-negative net counts from configured Darshan Completed camera lines, plus any documented manual corrections.</p>
        </>}
      </section>
    </main>
  );
}
